import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'

/**
 * app 横断の「現在時刻」 の単一の入口。
 *
 * ## なぜブラウザ時計をそのまま使わないか
 *
 * frontend には「今日か / 過去日か」 を判定して編集可否や GC ロックを決める箇所が
 * ある (`canMutate` → [[isTargetDateBeforeTodayInJst]] など)。 これをブラウザ時計で
 * 判定すると、 サーバーの判定とズレたときに 「押せるのに保存が失敗する」 状態が生まれ、
 * `docs/principles.md` 鉄則 1 「表示と実行の乖離は許容しない」 が破れる。
 *
 * ズレる原因は 2 つある:
 *
 * 1. 利用者の PC の時計が単純に狂っている (本番でも起こりうる)
 * 2. dev 環境の eAuth per-user 時刻偽装 (`dev_user_conf.fake_time`)。 backend の
 *    `timeutil.Clock` だけが偽装時刻を返し、 ブラウザは実時刻のままになる
 *    (`go/internal/backend/adapter/grpc/interceptor/faketime.go`)
 *
 * どちらも 「サーバーの now を正とする」 ことで同時に解ける。
 *
 * ## 仕組み
 *
 * `GetCurrentSession` レスポンスの `server_time` (backend の `timeutil.Clock` 由来)
 * とブラウザ時計の差を offset として保持し、 [[now]] がそれを加算して返す。 サーバー
 * 時刻を毎回問い合わせるわけではない (1 秒刻みのカウントダウンが成立しない)。 offset は
 * 「サーバーとブラウザの時計の差」 なので、 保持している間にずれるのはブラウザの
 * クォーツ精度分だけで実害が無い。
 *
 * ただし dev の時刻偽装が固定モード (`fake_time.mode = fixed`) の場合、 backend は
 * 同じ instant を返し続けるのに [[now]] はブラウザ時計ぶん進むため、 前回同期からの
 * 実経過時間だけ frontend が先へ出る。 offset は TickerBar の session 再取得
 * (5 分間隔) ごとに引き直されるのでズレはそこで上限が付き自己回復するが、 締切を
 * 実時間でまたぐ QA は単調モード (`monotonic`) で行う。 固定モードかどうかを
 * frontend が知って時計を止める設計は採らない — 偽装の存在を frontend に露出させない
 * のが本モジュールの前提で、 かつ時計が止まるとカウントダウン表示自体が成立しない。
 *
 * ## 安全性 (これが原因で画面を壊さないための設計)
 *
 * - offset 未設定時 (= 起動直後・取得失敗・server_time 未設定) の [[now]] は
 *   `new Date()` と完全に同一値を返す。 偽装が設定されていない環境ではこのモジュール
 *   全体が no-op になる
 * - [[applyServerTime]] は例外を投げない。 不正な値は黙って無視して直前の offset を保つ
 * - 明らかに壊れた server_time ([[isPlausibleServerTime]] の範囲外) は採用しない。
 *   proto の zero Timestamp が 1970-01-01 として流れてくる事故で全画面の日付判定が
 *   壊れるのを防ぐ
 */

/** ブラウザ時計に対するサーバー時刻の差 (ms)。 未取得は 0 = ブラウザ時計そのまま。 */
let offsetMs = 0

/**
 * offset 変更の購読者。
 *
 * offset は session RPC 完了時 (= 初回描画より後) に 1 度変わりうる。 購読口が無いと、
 * 既に mount 済みの時計消費者 ([[useNowByMinute]] 等) がブラウザ時計由来の値と
 * タイマー予約を保持し続け、 偽装時刻へ即座に揃わない。
 */
const offsetListeners = new Set<() => void>()

/** offset 変更を購読する。 戻り値の関数で解除する (`useSyncExternalStore` 互換)。 */
export function subscribeServerTimeOffset(listener: () => void): () => void {
  offsetListeners.add(listener)
  return () => {
    offsetListeners.delete(listener)
  }
}

/** offset を更新し、 実際に変わった時だけ購読者へ通知する。 */
function setOffsetMs(next: number): void {
  if (next === offsetMs) return
  offsetMs = next
  for (const listener of offsetListeners) listener()
}

/**
 * 採用可能な server_time の範囲。
 *
 * 下限は proto の zero Timestamp (= 1970-01-01) を弾くため。 未設定 field は
 * `undefined` で届くのが正だが、 zero 値が明示的に入って届いた場合に全画面の
 * 「今日」 が 1970 年になるのは影響が大きすぎるので、 値そのものでも防ぐ。
 *
 * 上限に実時刻からの相対的な上限 (「1 年以内」 等) を設けていないのは、 時刻偽装が
 * まさに実時刻から大きく離れた値を入れる機能だから。 絶対値としてありえない範囲だけ
 * を弾く。
 */
const MIN_PLAUSIBLE_MS = Date.UTC(2000, 0, 1)
const MAX_PLAUSIBLE_MS = Date.UTC(2200, 0, 1)

function isPlausibleServerTime(ms: number): boolean {
  return Number.isFinite(ms) && ms >= MIN_PLAUSIBLE_MS && ms <= MAX_PLAUSIBLE_MS
}

/**
 * サーバー時刻を取り込んで offset を更新する。
 *
 * `undefined` (backend が未設定) や採用範囲外の値は無視し、 直前の offset を保つ
 * (= 何も取り込まれていなければブラウザ時計のまま)。 呼び出し側でエラー処理が要らない
 * よう、 いかなる入力でも throw しない。
 *
 * `receivedAtMs` は `serverTime` を受け取ったブラウザ時計 (= 差を取る基準)。 呼び出し
 * 時点の `Date.now()` を基準にすると、 受信から取り込みまでの間隔がそのまま offset の
 * 誤差になる (キャッシュ済みレスポンスを後から取り込む場合はその滞留時間ぶんまるごと
 * 時計が過去へずれる)。 省略時は `Date.now()` を使う。
 */
export function applyServerTime(
  serverTime: Timestamp | undefined,
  receivedAtMs?: number,
): void {
  if (serverTime === undefined) return
  const serverMs = timestampDate(serverTime).getTime()
  if (!isPlausibleServerTime(serverMs)) return
  const baselineMs =
    receivedAtMs !== undefined &&
    Number.isFinite(receivedAtMs) &&
    receivedAtMs > 0
      ? receivedAtMs
      : Date.now()
  setOffsetMs(serverMs - baselineMs)
}

/**
 * 現在時刻。 時刻に依存する表示・判定はブラウザ時計 (`new Date()` / `Date.now()`)
 * ではなくこれを使う。
 *
 * offset 未取得なら `new Date()` と同一値。
 */
export function now(): Date {
  return new Date(Date.now() + offsetMs)
}

/** 現在の offset (ms)。 テストと、 偽装が効いているかの目視確認用。 */
export function serverTimeOffsetMs(): number {
  return offsetMs
}

/**
 * offset を未取得状態へ戻す。 テスト間で状態が漏れるのを防ぐためのもので、
 * プロダクトコードからは呼ばない。
 */
export function resetServerTimeOffsetForTest(): void {
  setOffsetMs(0)
}
